import { Fragment, useContext, useEffect, useState } from 'react';
import { format, isMatch } from "date-fns";
import Client from "../../models/serviceModels/Client";
import HouseholdMember from "../../models/serviceModels/HouseholdMember";
import { formatJSONDateWithoutTime, getDisplayAgeFromDateOfBirth, parseJSONDateWithoutTime } from "../../utils/dateHelpers";
import Button from './Button';
import Modal from './Modal';
import ErrorMessages from './ErrorMessages';
import Input from './Input';
import Checkbox from './Checkbox';
import {
    activateClient,
    assignClientsToUsers,
    unAssignClientsFromUsers,
    deactivateClient,
    deleteClient,
    makeHouseholdMemberHeadOfHousehold,
    promoteHouseholdMembers
} from '../../services/clientsService';
import TruncatedText from './TruncatedText';
import ParticipantFiles from './ParticipantFiles';
import { MeContext, OrganizationContext } from '../../App';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import IconButton from './IconButton';
import { ADMIN_ROLE_ID, CONTRIBUTOR_ROLE_ID } from '../../constants/userRoles';
import Dropdown from './Dropdown';
import getclassNames from '../../utils/getClassNames';
import { v4 as uuidv4 } from 'uuid';
import Select from "./Select";
import { searchUsers } from "../../services/userService";
import { assignedUserEnabled, householdEditorEnabled, householdIdentifiersEnabled, linkedHouseholdParticipantsEnabled } from '../../utils/features';
import { getCurrentLocationPath, setReturnLocation } from '../../utils/locationReturnService';
import { getDisplayGenderName, getDisplayRaceNames, getEthnicityFieldVisible, getRaceFieldLabel, isDemographicVisible } from '../../utils/demographicsSchema';
import { getEnrolledPrograms } from '../../services/programsService';
import "./ParticipantProfileHeader.css";

interface ParticipantProfileHeaderProps {
    client: Client,
    hideEdit?: boolean;
    isExpanded: boolean;
    onExpandChange: (expanded: boolean) => void;
    onUpdated: () => void;
}

const defaultDate = format(new Date(), 'yyyy-MM-dd');
const DEACTIVATION_UNENROLL_REASON = 'Participant is being deactivated';

interface DeactivationProgram {
    id: string;
    name: string;
    start?: string;
}

const ParticipantProfileHeader = ({ client, hideEdit = false, isExpanded, onUpdated, onExpandChange }: ParticipantProfileHeaderProps) => {
    const [showHousehold, setShowHousehold] = useState(false);
    const [showActivation, setShowActivation] = useState(false);
    const [activatedDate, setActivatedDate] = useState(defaultDate);
    const [deactivatedDate, setDeactivatedDate] = useState(defaultDate);
    const [unenrollPrograms, setUnenrollPrograms] = useState(false);
    const [unenrollProgramsDate, setUnenrollProgramsDate] = useState(defaultDate);
    const [unenrollProgramsReason, setUnenrollProgramsReason] = useState(DEACTIVATION_UNENROLL_REASON);
    const [deactivationActivePrograms, setDeactivationActivePrograms] = useState<DeactivationProgram[]>([]);
    const [loadingDeactivationPrograms, setLoadingDeactivationPrograms] = useState(false);
    const [showDeactivation, setShowDeactivation] = useState(false);
    const [modalErrors, setModalErrors] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [confirmDeletion, setConfirmDeletion] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showUnassignModal, setShowUnassignModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<string>("");
    const [isAssigning, setIsAssigning] = useState(false);
    const [isUnassigning, setIsUnassigning] = useState(false);
    const [assignableUsers, setAssignableUsers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
    const [promotingHouseholdGroupKey, setPromotingHouseholdGroupKey] = useState('');
    const [makingHeadOfHouseholdGroupKey, setMakingHeadOfHouseholdGroupKey] = useState('');
    const [householdActionErrors, setHouseholdActionErrors] = useState<string[]>([]);
    const [selectedHouseholdMembersByGroup, setSelectedHouseholdMembersByGroup] = useState<Record<string, string[]>>({});
    const me = useContext(MeContext);
    const organization = useContext(OrganizationContext);
    const navigate = useNavigate();
    const location = useLocation();
    const showPronouns = isDemographicVisible(organization.value, 'pronouns');
    const showGender = isDemographicVisible(organization.value, 'gender');
    const showEthnicity = getEthnicityFieldVisible(organization.value);
    const showRace = isDemographicVisible(organization.value, 'race');
    const showAddress = isDemographicVisible(organization.value, 'address');
    const showCity = isDemographicVisible(organization.value, 'city');
    const showState = isDemographicVisible(organization.value, 'state');
    const showZip = isDemographicVisible(organization.value, 'zip');
    const showPhone = isDemographicVisible(organization.value, 'phone');
    const showEmail = isDemographicVisible(organization.value, 'email');
    const raceFieldLabel = getRaceFieldLabel(organization.value);
    const fallbackActivePrograms = (client.programs || [])
        .filter(program => !program.programStop)
        .map(program => ({
            id: program.programId,
            name: program.programName,
            start: program.programStart
        }));
    const activePrograms = showDeactivation ? deactivationActivePrograms : fallbackActivePrograms;

    useEffect(() => {
        setReturnLocation(getCurrentLocationPath(location));
    }, [location.pathname, location.search, location.hash]);

    useEffect(() => {
        if (!showAssignModal || assignableUsers.length > 0) return;

        const fetchUsers = async () => {
            setIsAssigning(true);
            setModalErrors([]);

            try {
                const res = await searchUsers("", 0, 10000);
                res.data = res.data.filter(u => !u.deactivated);
                setAssignableUsers(res.data);
            } catch (e) {
                console.error(e);
                setModalErrors(["Failed to load users."]);
            } finally {
                setIsAssigning(false);
            }
        };

        fetchUsers();
    }, [showAssignModal, assignableUsers.length]);

    useEffect(() => {
        setSelectedHouseholdMembersByGroup({});
        setHouseholdActionErrors([]);
        setPromotingHouseholdGroupKey('');
        setMakingHeadOfHouseholdGroupKey('');
    }, [client.id, client.householdMembers, client.linkedHouseholds]);


    const handleShowActivation = () => {
        setModalErrors([]);
        setShowActivation(true);
        setSubmitting(false);
    };

    const handleShowDeactivation = () => {
        const today = format(new Date(), 'yyyy-MM-dd');
        const fallbackPrograms = (client.programs || [])
            .filter(program => !program.programStop)
            .map(program => ({
                id: program.programId,
                name: program.programName,
                start: program.programStart
            }));

        setModalErrors([]);
        setShowDeactivation(true);
        setDeactivatedDate(today);
        setDeactivationActivePrograms(fallbackPrograms);
        setUnenrollPrograms(fallbackPrograms.length > 0);
        setUnenrollProgramsDate(today);
        setUnenrollProgramsReason(DEACTIVATION_UNENROLL_REASON);
        setSubmitting(false);
        setLoadingDeactivationPrograms(true);

        getEnrolledPrograms(client.id).then(programs => {
            const currentPrograms = programs.map(program => ({
                id: program.id,
                name: program.name,
                start: program.start
            }));

            setDeactivationActivePrograms(currentPrograms);
            setUnenrollPrograms(currentPrograms.length > 0);
        }).catch(e => {
            console.error(e);
            setModalErrors(['Failed to load active program enrollments']);
        }).finally(() => {
            setLoadingDeactivationPrograms(false);
        });
    };

    const handleActivation = () => {
        if (!activatedDate) {
            setModalErrors(['Activate Date is required']);
            return;
        }

        if (!isMatch(activatedDate, 'yyyy-MM-dd')) {
            setModalErrors(['Activate Date is invalid']);
            return;
        }

        setSubmitting(true);

        activateClient({
            clientId: client.id,
            date: activatedDate
        }).then(() => {
            setShowActivation(false);
            if (onUpdated) {
                onUpdated();
            }
        }).catch(e => {
            console.error(e);
            setModalErrors(['Failed to activate client']);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const handleDeactivation = () => {
        if (!deactivatedDate) {
            setModalErrors(['Deactivate Date is required']);
            return;
        }

        if (!isMatch(deactivatedDate, 'yyyy-MM-dd')) {
            setModalErrors(['Deactivate Date is invalid']);
            return;
        }

        if (unenrollPrograms) {
            if (!unenrollProgramsDate) {
                setModalErrors(['Program Exit Date is required']);
                return;
            }

            if (!isMatch(unenrollProgramsDate, 'yyyy-MM-dd')) {
                setModalErrors(['Program Exit Date is invalid']);
                return;
            }

            const invalidProgram = activePrograms.find(program => program.start && new Date(unenrollProgramsDate) < parseJSONDateWithoutTime(program.start));
            if (invalidProgram) {
                setModalErrors([`Program Exit Date must be equal to or after the enrollment date for ${invalidProgram.name}`]);
                return;
            }
        }

        setSubmitting(true);

        deactivateClient({
            clientId: client.id,
            date: deactivatedDate,
            unenrollPrograms,
            unenrollProgramsDate: unenrollPrograms ? unenrollProgramsDate : undefined,
            unenrollProgramsReason: unenrollPrograms ? unenrollProgramsReason : undefined
        }).then(() => {
            setShowDeactivation(false);
            if (onUpdated) {
                onUpdated();
            }
        }).catch(e => {
            console.error(e);
            setModalErrors(['Failed to deactivate client']);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const handleDeletion = () => {
        setSubmitting(true);
        setModalErrors([]);

        deleteClient(client.id).then(() => {
            navigate('/', {
                replace: true
            });
        }).catch(e => {
            console.error(e);
            setModalErrors(['Failed to delete participant']);
        }).finally(() => {
            setSubmitting(false);
        });
    };

    const handleShowConfirmDeletion = () => {
        setModalErrors([]);
        setConfirmDeletion(true);
        setSubmitting(false);
    };

    const handleAssignToUser = async () => {
        setModalErrors([]);
        setIsAssigning(true);

        try {
            if (!selectedUser) {
                setModalErrors(["Please select a user."]);
                return;
            }
            const assignClient = [client.id]
            await assignClientsToUsers(assignClient, selectedUser);
            setShowAssignModal(false);
            if (onUpdated) onUpdated();
        } catch (e) {
            console.error(e);
            setModalErrors(["Something went wrong. Failed to assign participant."]);
        } finally {
            setIsAssigning(false);
        }
    };

    const handleUnAssignFromUser = async () => {
        setModalErrors([]);
        setIsUnassigning(true);

        try {
            if (!client.assignedTo?.id) {
                setModalErrors(["This participant is not assigned to any user."]);
                return;
            }
            const unassignClient = [{
                clientId: client.id,
                userId: client.assignedTo.id,
            }]
            await unAssignClientsFromUsers(unassignClient);
            setShowUnassignModal(false);
            if (onUpdated) onUpdated();
        } catch (e) {
            console.error(e);
            setModalErrors(["Something went wrong. Failed to unassign participant."]);
        } finally {
            setIsUnassigning(false);
        }
    };

    const getHouseholdGroupKey = (ownerClientId: string, groupType: 'own' | 'linked') => `${groupType}:${ownerClientId}`;

    const getSelectedHouseholdMembers = (groupKey: string, members: HouseholdMember[]) => {
        const selectedIds = selectedHouseholdMembersByGroup[groupKey] || [];
        return members.filter(member => selectedIds.includes(member.id));
    };

    const toggleHouseholdMemberSelection = (groupKey: string, householdMemberId: string, checked: boolean) => {
        setSelectedHouseholdMembersByGroup(current => {
            const selectedIds = current[groupKey] || [];

            return {
                ...current,
                [groupKey]: checked
                    ? [...selectedIds, householdMemberId].filter((id, index, values) => values.indexOf(id) === index)
                    : selectedIds.filter(id => id !== householdMemberId)
            };
        });
    };

    const clearHouseholdMemberSelection = (groupKey: string) => {
        setSelectedHouseholdMembersByGroup(current => {
            const updated = { ...current };
            delete updated[groupKey];
            return updated;
        });
    };

    const handlePromoteHouseholdMembers = async (ownerClientId: string, groupKey: string, householdMemberIds: string[]) => {
        setHouseholdActionErrors([]);
        setPromotingHouseholdGroupKey(groupKey);

        try {
            await promoteHouseholdMembers({
                clientId: ownerClientId,
                householdMemberIds
            });

            clearHouseholdMemberSelection(groupKey);

            if (onUpdated) {
                onUpdated();
            }
        } catch (e) {
            console.error(e);
            setHouseholdActionErrors(['Failed to promote household member(s) to participant.']);
        } finally {
            setPromotingHouseholdGroupKey('');
        }
    };

    const handleMakeHeadOfHousehold = async (ownerClientId: string, householdMemberId: string, groupKey: string) => {
        setHouseholdActionErrors([]);
        setMakingHeadOfHouseholdGroupKey(groupKey);

        try {
            await makeHouseholdMemberHeadOfHousehold({
                clientId: ownerClientId,
                householdMemberId
            });

            clearHouseholdMemberSelection(groupKey);

            if (onUpdated) {
                onUpdated();
            }
        } catch (e) {
            console.error(e);
            setHouseholdActionErrors(['Failed to make household member head of household.']);
        } finally {
            setMakingHeadOfHouseholdGroupKey('');
        }
    };


    const options = [];
    const canEditParticipants = (me.user?.userRoleId === ADMIN_ROLE_ID || me.user?.userRoleId === CONTRIBUTOR_ROLE_ID) && !hideEdit;
    const showHouseholdBulkActions = linkedHouseholdParticipantsEnabled && canEditParticipants;
    const showEnhancedDirectHouseholdPresentation = linkedHouseholdParticipantsEnabled;
    const ownHouseholdMembers = client.householdMembers || [];
    const linkedHouseholds = linkedHouseholdParticipantsEnabled ? (client.linkedHouseholds || []).filter(household => household.members.length > 0) : [];
    const ownHouseholdOwnerDisplayName = [client.firstName, client.middleName, client.lastName].filter(v => !!v).join(' ').trim() || 'Participant';
    const hasOwnHousehold = ownHouseholdMembers.length > 0 || (householdIdentifiersEnabled && client.householdId != null);
    const showLinkedHouseholdsFirst = !hasOwnHousehold && linkedHouseholds.length > 0;
    const linkedHouseholdsHeading = `${ownHouseholdOwnerDisplayName} ${hasOwnHousehold ? 'is also linked to the following households' : 'is linked to the following households'}`;
    const ownHouseholdCount = hasOwnHousehold
        ? ownHouseholdMembers.length + (showEnhancedDirectHouseholdPresentation ? 1 : 0)
        : 0;
    const linkedHouseholdCount = linkedHouseholds.reduce((total, household) => total + household.members.length + 1, 0);
    const householdButtonText = `${showHousehold ? 'Hide Household Members' : 'Show Household Members'} (${ownHouseholdCount + linkedHouseholdCount})`;
    const householdActionsLoading = !!promotingHouseholdGroupKey || !!makingHeadOfHouseholdGroupKey;
    const hasVisibleHouseholdId = (householdId?: number | string) => householdIdentifiersEnabled && householdId != null && householdId !== '';
    const renderHouseholdId = (householdId?: number | string) => !hasVisibleHouseholdId(householdId)
        ? null
        : <span className="household-id-label">Household ID: {householdId}</span>;
    const renderHouseholdHeading = (heading: 'h3' | 'h4', name: string, householdId?: number | string, actionElement?: JSX.Element | null) => {
        const titleElement = heading === 'h3'
            ? <h3>{name} Household</h3>
            : <h4>{name} Household</h4>;

        if (!hasVisibleHouseholdId(householdId)) {
            return (
                <div className="household-section-heading">
                    {titleElement}
                    {actionElement}
                </div>
            );
        }

        return (
            <div className="household-section-heading with-household-id">
                <div className="household-section-title-row">
                    {titleElement}
                    {actionElement}
                </div>
                <div className="household-section-id-row">
                    {renderHouseholdId(householdId)}
                </div>
            </div>
        );
    };

    const renderHouseholdActionBar = (groupKey: string, ownerClientId: string, members: HouseholdMember[], idPrefix: string) => {
        const selectedMembers = getSelectedHouseholdMembers(groupKey, members);
        const promotableMembers = selectedMembers.filter(member => !member.linkedClientId);
        const hasSelectedParticipant = selectedMembers.some(member => !!member.linkedClientId);
        const canPromoteSelectedMembers = showHouseholdBulkActions && selectedMembers.length > 0 && promotableMembers.length === selectedMembers.length && !hasSelectedParticipant;
        const canMakeSelectedMemberHead = showHouseholdBulkActions && selectedMembers.length === 1 && !!selectedMembers[0].linkedClientId;
        const showNoActionsAvailable = selectedMembers.length > 0 && !canPromoteSelectedMembers && !canMakeSelectedMemberHead;

        return (
            <div className="row align-items-center justify-content-between margin-bottom-2">
                <div className="column-auto">
                    <div className="row align-items-center">
                        {canPromoteSelectedMembers && <div className="column-auto">
                            <Button id={`${idPrefix}-promote-selected`}
                                text="Promote to Participant"
                                className="secondary small"
                                loading={promotingHouseholdGroupKey === groupKey}
                                disabled={householdActionsLoading && promotingHouseholdGroupKey !== groupKey}
                                onClick={() => handlePromoteHouseholdMembers(ownerClientId, groupKey, promotableMembers.map(member => member.id))} />
                        </div>}
                        {canMakeSelectedMemberHead && <div className="column-auto">
                            <Button id={`${idPrefix}-make-head-selected`}
                                text="Make Head of Household"
                                className="secondary small"
                                loading={makingHeadOfHouseholdGroupKey === groupKey}
                                disabled={householdActionsLoading && makingHeadOfHouseholdGroupKey !== groupKey}
                                onClick={() => handleMakeHeadOfHousehold(ownerClientId, selectedMembers[0].id, groupKey)} />
                        </div>}
                        {showNoActionsAvailable && <div className="column-auto">
                            <p className="margin-y-0 margin-start-2">No actions available</p>
                        </div>}
                    </div>
                </div>
                <div className="column-auto">
                    <p className="margin-y-0">
                        {selectedMembers.length > 0 ? `${selectedMembers.length} Selected` : '0 selected'}
                    </p>
                </div>
            </div>
        );
    };

    if (me.user?.userRoleId === ADMIN_ROLE_ID || me.user?.userRoleId === CONTRIBUTOR_ROLE_ID) {
        if (!hideEdit) {
            options.push({
                label: 'Merge',
                onClick: () => {
                    const mergeId = uuidv4()
                    localStorage.setItem(`mergeParticipants_${mergeId}`, JSON.stringify({
                        selectedClientId: client.id,
                        additionalClientIds: [],
                        expiresOn: new Date().getTime() + (1000 * 60 * 60 * 24 * 5)
                    }));
                    navigate(`/participants/merge/${mergeId}/search`);
                }
            });
        }

        if (client.activated && !client.deactivated) {
            options.push({
                label: 'Deactivate',
                onClick: handleShowDeactivation
            });
        } else {
            options.push({
                label: 'Activate',
                onClick: handleShowActivation
            });
        }

        if (assignedUserEnabled) {
            if (client.isAssigned && client.assignedTo) {
                options.push({
                    label: 'Unassign from User',
                    onClick: () => setShowUnassignModal(true)
                });
            } else {
                options.push({
                    label: 'Assign to User',
                    onClick: () => setShowAssignModal(true)
                });
            }
        }

        if (!hideEdit) {
            options.push({
                label: 'Delete',
                onClick: handleShowConfirmDeletion,
                className: 'text-danger'
            });
        }
    }

    return (
        <>
            <><div className="row justify-content-between align-items-center">
                <div className="column">
                    <div className={getclassNames("row align-items-center justify-content-between", isExpanded ? "border-bottom padding-bottom-5" : "")}>
                        <div className="column-auto">
                            <IconButton className="small flat"
                                id="expand-collapse-profile"
                                icon={isExpanded ? <i className="ri-arrow-up-s-line" /> : <i className="ri-arrow-down-s-line" />}
                                onClick={() => onExpandChange(!isExpanded)} />
                        </div>
                        <div className="column">
                            <div className="row align-items-center">
                                <div className="column-auto">
                                    <h2>{client.firstName} {client.middleName} {client.lastName}
                                        {showPronouns && client.pronouns && <div className="text-sub">{client.pronouns}</div>}
                                    </h2>
                                </div>
                                <div className="column-auto">
                                    <small>
                                        <div>Created - {format(new Date(client.created), 'MM/dd/yyyy')}</div>
                                        {(client.updated && <div>Updated - {format(new Date(client.updated), 'MM/dd/yyyy')}</div>) || null}
                                        {(client.activated && !client.deactivated && <div> Activated - {formatJSONDateWithoutTime(client.activated, 'MM/dd/yyyy')}</div>) || null}
                                        {(client.deactivated && <div> Deactivated - {formatJSONDateWithoutTime(client.deactivated, 'MM/dd/yyyy')}</div>) || null}
                                        {(assignedUserEnabled && (!client.isAssigned ? <div>Assignment - None</div> : <div>Assignment - {client.assignedTo?.firstName} {client.assignedTo?.lastName}</div>)) || null}
                                    </small>
                                </div>
                            </div>
                        </div>
                        {(canEditParticipants || options.length !== 0) && <div className="column-auto">
                            <div className="row align-items-center">
                                {canEditParticipants && <div className="column-auto">
                                    <Button id="edit-participant"
                                        text="Edit Participant"
                                        className="small"
                                        href={`/participants/${client.id}/edit`} />
                                </div>}
                                {options.length !== 0 && <div className="column-auto">
                                    <Dropdown options={options}
                                        id="client-options"
                                        optionSize="xlarge"
                                        alignRight={true} />
                                </div>}
                            </div>
                        </div>}
                    </div>
                    {isExpanded && <div className="row margin-vertical-5">
                        <div className="column-medium">
                            {showPhone && <div className="row margin-top-2">
                                <div className="column-auto">
                                    <label>Birth Date:</label> {client.dateOfBirth && `${formatJSONDateWithoutTime(client.dateOfBirth, 'MM/dd/yyyy')} (${getDisplayAgeFromDateOfBirth(client.dateOfBirth)})`}
                                </div>
                            </div>}
                            {showEmail && <div className="row margin-top-2">
                                <div className="column-auto">
                                    <label>Phone:</label> {client.phoneNumber || ''}
                                </div>
                            </div>}
                            <div className="row margin-top-2">
                                <div className="column-auto text-overlap-wrap maw-300">
                                    <label>Email:</label> {client.email || ''}
                                </div>
                            </div>
                            {showGender && <div className="row margin-top-2">
                                <div className="column-auto">
                                    <label>Gender:</label> {getDisplayGenderName(client.genderName, organization.value)}
                                </div>
                            </div>}
                            {showRace && <div className="row margin-top-2">
                                <div className="column-auto wrap-text inline-size-6">
                                    <label>{raceFieldLabel}:</label>&nbsp;
                                    <TruncatedText
                                        id="races"
                                        value={getDisplayRaceNames({
                                            ethnicityName: client.ethnicityName,
                                            races: client.races
                                        }, organization.value).join(', ')}
                                        maxLength={30}
                                        lengthVariance={10} />
                                </div>
                            </div>}
                            {showEthnicity && <div className="row margin-top-2">
                                <div className="column-auto">
                                    <label>Ethnicity:</label> {client.ethnicityName}
                                </div>
                            </div>}
                        </div>
                        <div className="column-medium">
                            {(showAddress || showCity || showState || showZip) && <div className="row margin-top-2">
                                <div className="column-auto">
                                    {showAddress && <><label>Address:</label><div className="maw-200">{client.addressLine1}</div>{client.addressLine2 && (<div>{client.addressLine2}</div>)}</>}
                                    {(showCity || showState || showZip) && <div>{showCity && client.addressCity}{showCity && (showState || showZip) && ', '}{showState && client.addressState}{showState && showZip && ' '}{showZip && client.addressZipCode}</div>}
                                </div>
                            </div>}
                        </div>
                        <div className="column-large-5">
                            <ParticipantFiles clientId={client.id} />
                        </div>
                    </div>}
                </div>
            </div>
                <>{isExpanded && <div className="row margin-top-2">
                    <div className="column-auto">
                        <Button id="show-household" text={householdButtonText}
                            icon={showHousehold ? (<i className="ri-arrow-up-s-line" />) : (<i className="ri-arrow-down-s-line" />)}
                            iconLocation="end"
                            className="secondary"
                            onClick={() => setShowHousehold(!showHousehold)} />
                    </div>
                </div>}
                </>
            </>
            {showHousehold && isExpanded && (<div className="row">
                <div className="column">
                    <div className="row">
                        <div className="column-auto">
                            <h2>Household Members</h2>
                        </div>
                    </div>
                    {householdActionErrors.length > 0 && <div className="row margin-bottom-2">
                        <div className="column-auto">
                            <ErrorMessages messages={householdActionErrors} />
                        </div>
                    </div>}
                    {!showLinkedHouseholdsFirst && ownHouseholdMembers.length === 0 && <>
                        <div className="row align-items-center margin-top-3">
                            <div className="column-auto">
                                {renderHouseholdHeading('h3', ownHouseholdOwnerDisplayName, client.householdId,
                                    householdEditorEnabled && canEditParticipants ? <>
                                        <span className="household-section-heading-separator">|</span>
                                        <Button id="start-own-household-members"
                                            text={`Start ${ownHouseholdOwnerDisplayName} Household`}
                                            className="flat small household-section-edit-link"
                                            href={`/households/${client.id}/edit`} />
                                    </> : null)}
                            </div>
                        </div>
                        <div className="row margin-bottom-3">
                            <div className="column-auto">
                                <small>No direct household members yet.</small>
                            </div>
                        </div>
                    </>}
                    {ownHouseholdMembers.length > 0 && <>
                        <div className="row align-items-center margin-top-3">
                            <div className="column-auto">
                                {renderHouseholdHeading('h3', ownHouseholdOwnerDisplayName, client.householdId,
                                    householdEditorEnabled && canEditParticipants ? <>
                                        <span className="household-section-heading-separator">|</span>
                                        <Button id="edit-own-household-members"
                                            text={`Edit ${ownHouseholdOwnerDisplayName} Household`}
                                            className="flat small household-section-edit-link"
                                            href={`/households/${client.id}/edit`} />
                                    </> : null)}
                            </div>
                        </div>
                        {showHouseholdBulkActions && renderHouseholdActionBar(getHouseholdGroupKey(client.id, 'own'), client.id, ownHouseholdMembers, 'own-household-actions')}
                        <div id="own-household-members-list" className="list-item-container">
                            {showEnhancedDirectHouseholdPresentation && <div className="list-item">
                                <div className="row align-items-top">
                                    <div className="column-medium-3">
                                        <label>Name:</label>
                                        <div><Link id="own-household-owner-view-profile" to={`/participants/${client.id}/profile-demographics`} target="_blank">{ownHouseholdOwnerDisplayName}</Link></div>
                                    </div>
                                    <div className="column-medium">
                                        <div className="row align-items-top">
                                            <div className="column-medium-3">
                                                <label>Relationship:</label>
                                                <div>Head of Household</div>
                                            </div>
                                            <div className="column-medium-3">
                                                <label>Birth Date:</label>
                                                <div>{client.dateOfBirth && `${formatJSONDateWithoutTime(client.dateOfBirth, 'MM/dd/yyyy')} (${getDisplayAgeFromDateOfBirth(client.dateOfBirth)})`}</div>
                                            </div>
                                            {showGender && <div className="column-medium-3">
                                                <label>Gender:</label>
                                                <div>{getDisplayGenderName(client.genderName, organization.value)}</div>
                                            </div>}
                                            {showRace && <div className="column-medium-3">
                                                <label>{raceFieldLabel}:</label>
                                                <div><TruncatedText
                                                    id="races"
                                                    value={getDisplayRaceNames({
                                                        ethnicityName: client.ethnicityName,
                                                        races: client.races
                                                    }, organization.value).join(', ')}
                                                    maxLength={15} /></div>
                                            </div>}
                                        </div>
                                    </div>
                                </div>
                                <div className="row align-items-center">
                                    <div className="column-medium-3">
                                        <label>Participant:</label>
                                        <div>Yes</div>
                                    </div>
                                    {showEmail && client.email && <div className="column-medium-3">
                                        <label>Email:</label>
                                        <div>{client.email}</div>
                                    </div>}
                                    {showPhone && client.phoneNumber && <div className="column-medium-3">
                                        <label>Phone Number:</label>
                                        <div>{client.phoneNumber}</div>
                                    </div>}
                                </div>
                                <div className="row justify-content-between align-items-center margin-top-2">
                                    <div className="column-auto">
                                        <div className="row align-items-center">
                                            <div className="column-medium-auto">
                                                <div id="head-of-household-indicator-own" className="household-member-designation-pill">Current Profile</div>
                                            </div>
                                            <div className="column-medium-auto">
                                                <div id="head-of-household-indicator-own-secondary" className="household-member-designation-pill">Head of Household</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>}
                            {ownHouseholdMembers.map((hhm, index) => {
                                const isCurrentProfile = hhm.linkedClientId === client.id;
                                const linkedProfileHref = hhm.linkedClientId ? `/participants/${hhm.linkedClientId}/profile-demographics` : '';
                                const ownGroupKey = getHouseholdGroupKey(client.id, 'own');
                                const isSelected = (selectedHouseholdMembersByGroup[ownGroupKey] || []).includes(hhm.id);

                                return (<Fragment key={hhm.id}>
                                <div className="list-item" key={hhm.id}>
                                    <div className="row align-items-top">
                                        {showHouseholdBulkActions && <div className="column-auto">
                                            <input id={`select-own-household-member-${index}`}
                                                type="checkbox"
                                                checked={isSelected}
                                                disabled={householdActionsLoading}
                                                onChange={e => toggleHouseholdMemberSelection(ownGroupKey, hhm.id, e.target.checked)} />
                                        </div>}
                                        <div className="column-medium-3">
                                            <label>Name:</label>
                                            <div>
                                                {hhm.linkedClientId ? <Link id={`linked-household-name-own-${index}`} to={linkedProfileHref} target="_blank">{hhm.firstName} {hhm.middleName} {hhm.lastName}</Link> : <>{hhm.firstName} {hhm.middleName} {hhm.lastName}</>}
                                            </div>
                                        </div>
                                        <div className="column-medium">
                                            <div className="row align-items-top">
                                                <div className="column-medium-3">
                                                    <label>Relationship:</label>
                                                    <div>{hhm.relationshipName}</div>
                                                </div>
                                                <div className="column-medium-3">
                                                    <label>Birth Date:</label>
                                                    <div>{hhm.dateOfBirth && format(parseJSONDateWithoutTime(hhm.dateOfBirth), 'MM/dd/yyyy')}</div>
                                                </div>
                                                {showGender && <div className="column-medium-3">
                                                    <label>Gender:</label>
                                                    <div>{getDisplayGenderName(hhm.genderName, organization.value)}</div>
                                                </div>}
                                                {showRace && <div className="column-medium-3">
                                                    <label>{raceFieldLabel}:</label>
                                                    <div><TruncatedText
                                                        id="races"
                                                        value={getDisplayRaceNames({
                                                            ethnicityName: hhm.ethnicityName,
                                                            races: hhm.races
                                                        }, organization.value).join(', ')}
                                                        maxLength={15} /></div>
                                                </div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row align-items-center">
                                        <div className="column-medium-3">
                                            <label>Participant:</label>
                                            <div>{hhm.linkedClientId ? 'Yes' : 'No'}</div>
                                        </div>
                                        {showEmail && hhm.email && <div className="column-medium-3">
                                            <label>Email:</label>
                                            <div>{hhm.email}</div>
                                        </div>}
                                        {showPhone && hhm.phoneNumber && <div className="column-medium-3">
                                            <label>Phone Number:</label>
                                            <div>{hhm.phoneNumber}</div>
                                        </div>}
                                    </div>
                                    {linkedHouseholdParticipantsEnabled && (isCurrentProfile || (hhm.linkedClientId && !isCurrentProfile)) && <div className="row justify-content-between align-items-center margin-top-2">
                                        <div className="column-auto">
                                            <div className="row align-items-center">
                                                {isCurrentProfile && <div className="column-medium-auto">
                                                    <div id={`current-household-profile-indicator-own-${index}`} className="household-member-designation-pill">Current Profile</div>
                                                </div>}
                                            </div>
                                        </div>
                                    </div>}
                                </div>
                            </Fragment>);
                        })}
                        </div>
                    </>}                    
                    {linkedHouseholds.length !== 0 && <div className="row margin-bottom-2">
                        <div className="column-auto">
                            <h3>{linkedHouseholdsHeading}</h3>
                        </div>
                    </div>}
                    {linkedHouseholds.map((linkedHousehold, linkedHouseholdIndex) => {
                        const householdOwnerClientId = linkedHousehold.owner.clientId;
                        const householdOwnerDisplayName = [linkedHousehold.owner.firstName, linkedHousehold.owner.middleName, linkedHousehold.owner.lastName]
                            .filter(v => !!v)
                            .join(' ')
                            .trim() || 'Household Owner';

                        return (<Fragment key={householdOwnerClientId}>
                            <div className="row align-items-center margin-top-5">
                                <div className="column-auto">
                                    {renderHouseholdHeading('h4', householdOwnerDisplayName, linkedHousehold.owner.householdId,
                                        householdEditorEnabled && canEditParticipants ? <>
                                            <span className="household-section-heading-separator">|</span>
                                            <Button id={`edit-linked-household-members-${linkedHouseholdIndex}`}
                                                text={`Edit ${householdOwnerDisplayName} Household`}
                                                className="flat small household-section-edit-link"
                                                href={`/households/${householdOwnerClientId}/edit`} />
                                        </> : null)}
                                </div>
                            </div>
                            {showHouseholdBulkActions && renderHouseholdActionBar(getHouseholdGroupKey(householdOwnerClientId, 'linked'), householdOwnerClientId, linkedHousehold.members, `linked-household-actions-${linkedHouseholdIndex}`)}
                            <div id={`linked-household-members-list-${linkedHouseholdIndex}`} className="list-item-container">
                                <div className="list-item">
                                    <div className="row align-items-top">
                                        <div className="column-medium-3">
                                            <label>Name:</label>
                                            <div><Link id={`linked-household-owner-name-${linkedHouseholdIndex}`} to={`/participants/${householdOwnerClientId}/profile-demographics`} target="_blank">{householdOwnerDisplayName}</Link></div>
                                        </div>
                                        <div className="column-medium">
                                            <div className="row align-items-top">
                                                <div className="column-medium-3">
                                                    <label>Relationship:</label>
                                                    <div>Head of Household</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row align-items-center margin-top-2">
                                        <div className="column-medium-3">
                                            <label>Participant:</label>
                                            <div>Yes</div>
                                        </div>
                                    </div>
                                    {linkedHouseholdParticipantsEnabled && <div className="row justify-content-between align-items-center margin-top-2">
                                        <div className="column-auto">
                                            <div className="row align-items-center">
                                                <div className="column-medium-auto">
                                                    <div id={`head-of-household-indicator-linked-${linkedHouseholdIndex}`} className="household-member-designation-pill">Head of Household</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>}
                            </div>
                            {linkedHousehold.members.map((hhm, index) => {
                                const isCurrentProfile = hhm.linkedClientId === client.id;
                                const linkedProfileHref = hhm.linkedClientId ? `/participants/${hhm.linkedClientId}/profile-demographics` : '';
                                const linkedGroupKey = getHouseholdGroupKey(householdOwnerClientId, 'linked');
                                const isSelected = (selectedHouseholdMembersByGroup[linkedGroupKey] || []).includes(hhm.id);

                                return (<Fragment key={hhm.id}>
                                    <div className="list-item" key={hhm.id}>
                                        <div className="row align-items-top">
                                            {showHouseholdBulkActions && <div className="column-auto">
                                                <input id={`select-linked-household-member-${linkedHouseholdIndex}-${index}`}
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    disabled={householdActionsLoading}
                                                    onChange={e => toggleHouseholdMemberSelection(linkedGroupKey, hhm.id, e.target.checked)} />
                                            </div>}
                                            <div className="column-medium-3">
                                                <label>Name:</label>
                                                <div>
                                                    {hhm.linkedClientId ? <Link id={`linked-household-name-linked-${linkedHouseholdIndex}-${index}`} to={linkedProfileHref} target="_blank">{hhm.firstName} {hhm.middleName} {hhm.lastName}</Link> : <>{hhm.firstName} {hhm.middleName} {hhm.lastName}</>}
                                                </div>
                                            </div>
                                            <div className="column-medium">
                                                <div className="row align-items-top">
                                                        <div className="column-medium-3">
                                                            <label>Relationship:</label>
                                                            <div>{hhm.relationshipName}</div>
                                                        </div>
                                                        <div className="column-medium-3">
                                                            <label>Birth Date:</label>
                                                            <div>{hhm.dateOfBirth && format(parseJSONDateWithoutTime(hhm.dateOfBirth), 'MM/dd/yyyy')}</div>
                                                        </div>
                                                        {showGender && <div className="column-medium-3">
                                                            <label>Gender:</label>
                                                            <div>{getDisplayGenderName(hhm.genderName, organization.value)}</div>
                                                        </div>}
                                                        {showRace && <div className="column-medium-3">
                                                            <label>{raceFieldLabel}:</label>
                                                            <div><TruncatedText
                                                                id="races"
                                                                value={getDisplayRaceNames({
                                                                    ethnicityName: hhm.ethnicityName,
                                                                    races: hhm.races
                                                                }, organization.value).join(', ')}
                                                                maxLength={15} /></div>
                                                        </div>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row align-items-center">
                                                <div className="column-medium-3">
                                                    <label>Participant:</label>
                                                    <div>{hhm.linkedClientId ? 'Yes' : 'No'}</div>
                                                </div>
                                                {hhm.email && <div className="column-medium-3">
                                                    <label>Email:</label>
                                                    <div>{hhm.email}</div>
                                                </div>}
                                                {hhm.phoneNumber && <div className="column-medium-3">
                                                    <label>Phone Number:</label>
                                                    <div>{hhm.phoneNumber}</div>
                                                </div>}
                                            </div>
                                            {linkedHouseholdParticipantsEnabled && ((hhm.linkedClientId && !isCurrentProfile) || isCurrentProfile) && <div className="row justify-content-between align-items-center margin-top-2">
                                                <div className="column-auto">
                                                    <div className="row align-items-center">
                                                        {isCurrentProfile && <div className="column-medium-auto">
                                                            <div id={`current-household-profile-indicator-linked-${linkedHouseholdIndex}-${index}`} className="household-member-designation-pill">Current Profile</div>
                                                        </div>}
                                                    </div>
                                                </div>
                                            </div>}
                                        </div>
                                    </Fragment>);
                                })}
                            </div>
                        </Fragment>);
                    })}
                    {showLinkedHouseholdsFirst && <>
                        <div className="row align-items-center margin-top-3">
                            <div className="column-auto">
                                {renderHouseholdHeading('h3', ownHouseholdOwnerDisplayName, client.householdId,
                                    householdEditorEnabled && canEditParticipants ? <>
                                        <span className="household-section-heading-separator">|</span>
                                        <Button id="start-own-household-members"
                                            text={`Start ${ownHouseholdOwnerDisplayName} Household`}
                                            className="flat small household-section-edit-link"
                                            href={`/households/${client.id}/edit`} />
                                    </> : null)}
                            </div>
                        </div>
                        <div className="row margin-bottom-3">
                            <div className="column-auto">
                                <small>No direct household members yet.</small>
                            </div>
                        </div>
                    </>}
                </div>
            </div>)}
            <Modal id="delete-modal" visible={confirmDeletion} className="small" title="Confirm Delete Participant" onClose={() => setConfirmDeletion(false)}>
                <>
                    {modalErrors && <ErrorMessages messages={modalErrors} />}
                    <div className="row">
                        <div className="column">
                            <div>Are you sure you want to delete this participant?</div>
                            <div>This action is irreversible.</div>
                            <div>All created data associated with this participant will also be deleted.</div>
                            <ul>
                                <li>Case Plans</li>
                                <li>Files</li>
                                <li>Notes</li>
                                <li>Program Assignments</li>
                                <li>Report Results</li>
                                <li>Submitted Forms</li>
                            </ul>
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-auto">
                                    <Button id="cancel-delete"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={submitting}
                                        onClick={() => setConfirmDeletion(false)} />
                                </div>
                                <div className="column-auto">
                                    <Button id="execute-delete"
                                        text="Delete"
                                        className="warning"
                                        disabled={submitting}
                                        loading={submitting}
                                        onClick={handleDeletion} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            </Modal>
            <Modal id="activate-modal" visible={showActivation} className="small" title="Activate Participant" onClose={() => setShowActivation(false)}>
                <>
                    {modalErrors && <ErrorMessages messages={modalErrors} />}
                    <div className="row">
                        <div className="column">
                            <Input id="active-date" type="date" label="Provide an Active Date" value={activatedDate} onChange={(e) => setActivatedDate(e)} />
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-activate"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={submitting}
                                        onClick={() => setShowActivation(false)} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-activate"
                                        text="Activate"
                                        disabled={submitting}
                                        loading={submitting}
                                        onClick={handleActivation} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            </Modal>
            <Modal id="deactivate-modal" visible={showDeactivation} className="small" title="Confirm Deactivate Participant" onClose={() => setShowDeactivation(false)}>
                <>
                    {modalErrors && <ErrorMessages messages={modalErrors} />}
                    <div className="row">
                        <div className="column">
                            <Input id="deactive-date" type="date"
                                label="Provide a Deactivate Date"
                                required={true}
                                value={deactivatedDate}
                                onChange={(e) => setDeactivatedDate(e)} />
                            {loadingDeactivationPrograms && <div className="margin-top-4">Loading active program enrollments...</div>}
                            {activePrograms.length > 0 && <>
                                <div className="margin-top-4">
                                    <div>Active Program Enrollments: {activePrograms.length}</div>
                                    <Checkbox id="unenroll-programs"
                                        checked={unenrollPrograms}
                                        disabled={submitting}
                                        onChange={setUnenrollPrograms}
                                        label="Unenroll from all active programs" />
                                </div>
                                {unenrollPrograms && <>
                                    <div className="margin-top-4">
                                        <Input id="unenroll-programs-date"
                                            type="date"
                                            label="Program Exit Date"
                                            required={true}
                                            disabled={submitting}
                                            value={unenrollProgramsDate}
                                            onChange={(e) => setUnenrollProgramsDate(e)} />
                                    </div>
                                    <div className="margin-top-4">
                                        <Input id="unenroll-programs-reason"
                                            type="multi"
                                            label="Program Exit Reason"
                                            disabled={submitting}
                                            value={unenrollProgramsReason}
                                            onChange={(e) => setUnenrollProgramsReason(e)} />
                                    </div>
                                </>}
                            </>}
                        </div>
                    </div>
                    <div className="row margin-top-5">
                        <div className="column">
                            <div className="row justify-content-between">
                                <div className="column-medium-auto">
                                    <Button id="cancel-deactivate"
                                        text="Cancel"
                                        className="secondary"
                                        disabled={submitting}
                                        onClick={() => setShowDeactivation(false)} />
                                </div>
                                <div className="column-medium-auto">
                                    <Button id="confirm-deactivate"
                                        text="Deactivate"
                                        disabled={submitting || loadingDeactivationPrograms}
                                        loading={submitting}
                                        onClick={handleDeactivation} />
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            </Modal>
            {/* Assign Modal */}
            <Modal
                id="assign-user-modal"
                title="Assign to User"
                className="small padding-horizontal-5"
                visible={showAssignModal}
                onClose={() => setShowAssignModal(false)}
            >
                <div className="row">
                    <div className="column">
                        <div className="padding-vertical-2">
                            <Select
                                id="users-list"
                                label="Select a user"
                                value={selectedUser}
                                onChange={(user) => setSelectedUser(user)}
                            >
                                <option value="">-- Select a user --</option>
                                {assignableUsers.map(user => (
                                    <option key={user.id} value={user.id}>
                                        {user.firstName} {user.lastName}
                                    </option>
                                ))}
                            </Select>

                            {modalErrors.length > 0 && <ErrorMessages messages={modalErrors} />}
                        </div>
                        <div className="row justify-content-between margin-top-4">
                            <div className="column-auto">
                                <Button
                                    id="cancel-assign"
                                    className="secondary"
                                    text="Cancel"
                                    onClick={() => setShowAssignModal(false)}
                                    disabled={isAssigning}
                                />
                            </div>
                            <div className="column-auto">
                                <Button
                                    id="confirm-assign"
                                    text="Confirm"
                                    disabled={!selectedUser || isAssigning}
                                    loading={isAssigning}
                                    onClick={handleAssignToUser}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Unassign Modal */}
            <Modal
                id="unassign-user-modal"
                title="Unassign from User"
                className="small padding-horizontal-5"
                visible={showUnassignModal}
                onClose={() => setShowUnassignModal(false)}
            >
                <div className="row">
                    <div className="column">
                        <p>
                            Are you sure you want to unassign this participant from{" "}
                            <strong>
                                {client.assignedTo?.firstName} {client.assignedTo?.lastName}
                            </strong>?
                        </p>

                        {modalErrors.length > 0 && <ErrorMessages messages={modalErrors} />}

                        <div className="row justify-content-between margin-top-4">
                            <div className="column-auto">
                                <Button
                                    id="cancel-unassign"
                                    className="secondary"
                                    text="Cancel"
                                    onClick={() => setShowUnassignModal(false)}
                                    disabled={isUnassigning}
                                />
                            </div>
                            <div className="column-auto">
                                <Button
                                    id="confirm-unassign"
                                    className="warning"
                                    text="Unassign"
                                    loading={isUnassigning}
                                    disabled={isUnassigning}
                                    onClick={handleUnAssignFromUser}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ParticipantProfileHeader;
